/*
    Copyright 2020 Lauren Gyldenlove.
    This file is part of Duelmaster.

    Duelmaster is free software: you can redistribute it and/or modify
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Duelmaster is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with Foobar.  If not, see <https://www.gnu.org/licenses/>.
 */


/**
 * This is a Polish Notation Calculator.
 */

enum TokenType {
    NUMERIC,
    OPERATOR,
    PAREN
};

type Token = {
    value: string,
    type: TokenType
};


function isBlank(value: string) {
    return /^\s+$/.test(value);
}

function isOperator(value: string) {
    return /[+\-/x*]/.test(value);
}

function isParen(value: string) {
    return /[()]/.test(value);
}

function isNumeric(value: string) {
    return /^[0-9]$/.test(value);
}

function hasValue(token: Token) {
    return token.value.length > 0;
}

function tokenizeCalculation(calculation: string): Array<Token> {
    let tokens = new Array<Token>();

    let charArray = Array.from(calculation);
    let currentIndex: number = 0;
    let currentToken: Token = { "value": "", "type": TokenType.NUMERIC };
    while(currentIndex < charArray.length) {
        let currentChar = calculation[currentIndex];

        if(hasValue(currentToken) && (isBlank(currentChar) || isOperator(currentChar) || isParen(currentChar))) {
            tokens.push(currentToken);
            currentToken = { "value": "", "type": TokenType.NUMERIC };
        }
        
        if(isOperator(currentChar)) {
            currentToken = { "value": "", "type": TokenType.NUMERIC };
            tokens.push({ "value": currentChar, "type": TokenType.OPERATOR });
        } else if(isParen(currentChar)) {
            currentToken = { "value": "", "type": TokenType.NUMERIC };
            tokens.push({ "value": currentChar, "type": TokenType.PAREN });
        } else if(isNumeric(currentChar)) {
            currentToken.value = currentToken.value.concat(currentChar);
        }

        currentIndex++;
    }
    
    if(hasValue(currentToken)) {
        tokens.push(currentToken);
    }

    return tokens;
}


function applyOperation(operation: Token, tokenStack: Array<Token>): number {
    let firstToken = tokenStack.pop();
    if(firstToken === undefined) {
        console.log("Error applying operation due to broken stack.");
        return 0;
    }

    let firstValue = Number(firstToken.value) || 0;
    return tokenStack.map((token) => Number(token.value) || 0).reduceRight((accum, currentValue) => {
        if(operation.value === '+') {
            return accum + currentValue;
        } else if(operation.value === '-') {
            return accum - currentValue;
        } else if(operation.value === '/') {
            return accum / currentValue;
        } else if(operation.value === '*' || operation.value === 'x') {
            return accum * currentValue;
        } 

        return accum;
    }, firstValue);
}

function stackTokens(tokens: Array<Token>, tokenStack: Array<Token>, value: number): number {
    if(tokens.length === 0) {
        return value;
    }

    let currentToken = tokens.pop();
    if(currentToken === undefined) {
        return value;
    }

    if(currentToken.type === TokenType.NUMERIC) {
        tokenStack.push(currentToken);

        // This exists so we can just input a single number.
        value = Number(currentToken.value) || 0; 
    } else if(currentToken.type === TokenType.OPERATOR && tokenStack.length > 1) {
        let token1 = tokenStack.pop();
        let token2 = tokenStack.pop();
        if(token1 && token2) {
            console.log("(" + currentToken.value + " " + token2.value + " " + token1.value + ")");
            let operationStack: Array<Token> = [token1, token2];
            value = applyOperation(currentToken, operationStack);
            tokenStack.push({ "value": value.toString(), "type": TokenType.NUMERIC });
        }
    }

    return stackTokens(tokens, tokenStack, value);
}

const INFIX_PRIORITY: { [key: string]: number } = {
    "x": 3,
    "/": 3,
    "*": 3,
    "+": 2,
    "-": 2
}

function infixConverter(tokens: Array<Token>): Array<Token> {
    let output: Array<Token> = [];
    let operatorStack: Array<Token> = [];

    tokens.forEach((token) => {
        if(token.type === TokenType.NUMERIC) {
            output.push(token);
        } else if(token.type === TokenType.OPERATOR) {
            let priorToken: Token | undefined = operatorStack[operatorStack.length - 1];
            while(priorToken !== undefined && priorToken.type === TokenType.OPERATOR && INFIX_PRIORITY[token.value] <= INFIX_PRIORITY[priorToken.value]) {
                output.push(priorToken);
                operatorStack.pop();
                priorToken = operatorStack[operatorStack.length - 1];
            }
            operatorStack.push(token);
        } else if(token.type === TokenType.PAREN && token.value === "(") {
            operatorStack.push(token);
        } else if(token.type === TokenType.PAREN && token.value === ")") {
            let priorToken: Token | undefined = operatorStack[operatorStack.length - 1];
            
            while(priorToken != null && priorToken.value !== "(") {
                priorToken = operatorStack.pop();
                if(priorToken != null && priorToken.value !== "(") {
                    console.log("Popped " + priorToken.value + " to output stack.");
                    output.push(priorToken);
                }
            }
        }
    });
    console.log(output);

    return operatorStack.concat(output.reverse());
}

export function parseCalculation(calculation: string): number {
    let tokens = tokenizeCalculation(calculation);
    let tokenStack = Array<Token>();
    let value = 0;

    let prefixConversion = infixConverter(tokens);
    console.log("Token stack:");
    console.log(prefixConversion.map((item) => {
        return item.value;
    }));
    console.log("Parsing stack:");
    return stackTokens(prefixConversion, tokenStack, value);
}
